import {
  CovideoGTMVideoAttributes,
  GTMVideoAttributes,
  CovideoGTMVehicleItem,
  GTMVehicleAttributes,
  GTMVideoMessageProperties,
  CovideoGTMVideoMessageProperties,
} from './types';

/**
 * Convert a duration to decimal minutes.
 * Pass API value (ms or s) and get minutes rounded to 2 decimals.
 *
 * @example toDecimalMinutes(50600, 'ms') // 0.84  (≈ 50.6s)
 * @example toDecimalMinutes(50, 's')     // 0.83  (50s)
 * @example toDecimalMinutes(165, 's')    // 2.75  (2m45s)
 */
export function toDecimalMinutes(
  value: number,
  unit: 'ms' | 's' = 'ms',
  fractionDigits = 2
): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  const seconds = unit === 'ms' ? value / 1000 : value;
  const minutes = seconds / 60;
  const factor = 10 ** fractionDigits;
  return Math.round(minutes * factor) / factor;
}

export function mapToGTMVideoAttributes(
  src?: CovideoGTMVideoAttributes
): GTMVideoAttributes {
  if (!src) {
    return {} as GTMVideoAttributes;
  }

  return {
    video_id: src?.videoId ? Number(src.videoId) : undefined,
    video_title: src.videoTitle || undefined,
    video_type: src.videoType ?? undefined,
    video_customer_name: Boolean(src.videoCustomerName),
    video_length: src.videoLength
      ? toDecimalMinutes(Number(src.videoLength))
      : undefined,
  };
}

/**
 * Map app-level meeting attributes to GTM meeting attributes.
 */

export function mapToGtmVehicleAttributes(
  item?: CovideoGTMVehicleItem
): GTMVehicleAttributes {
  const vehicleData: GTMVehicleAttributes = !!item
    ? {
        vehicle: item?.title || undefined,
        price: item?.price ? parseFloat(item?.price.toString()) : undefined,
        vin: item?.vin || undefined,
        stock_number: item?.stockNumber?.toString() || undefined,
        condition: item?.itemCondition || undefined,
        engine: item?.engine || undefined,
        make: item?.make || undefined,
        model: item?.model || undefined,
        trim: item?.trim || undefined,
        year: item?.year ? parseInt(item?.year.toString(), 10) : undefined,
        mileage: item?.mileage
          ? parseInt(item?.mileage.toString(), 10)
          : undefined,
        ext_color: item?.exteriorColor || undefined,
        int_color: item?.interiorColor || undefined,
        transmission: item?.transmission || undefined,
      }
    : {};
  return vehicleData;
}

export function mapToGtmVideoMessageProperties(
  item?: CovideoGTMVideoMessageProperties
): GTMVideoMessageProperties {
  if (!item) return {} as GTMVideoMessageProperties;

  return {
    customer_id: item?.customerId || undefined,
    sms: item?.sms || undefined,
    email: item?.email || undefined,
    notification_viewed: item?.notificationViewed || false,
    landing_page: item?.landingPage || undefined,
    call_to_action: item?.callToAction || undefined,
    thumbnails: item?.thumbnails || undefined,
  };
}
